import { Component, HostListener, OnInit, inject } from '@angular/core';
import { CommonModule, DatePipe } from '@angular/common';
import { LayoutComponent } from '../../core/layout/layout.component';
import { MatInputModule } from '@angular/material/input';
import { AgGridAngular } from 'ag-grid-angular';
import { ColDef } from 'ag-grid-community';
import { ActivatedRoute, Router } from '@angular/router';
import { ToastrService } from 'ngx-toastr';
import { ProjectService } from '../../features/projects/services/projects.service';
import { MatDialog } from '@angular/material/dialog';
import { ConfirmDialog } from '../../shared/components/confirm-dialog/confirm-dialog';
import { Subject } from 'rxjs';
import { debounceTime, distinctUntilChanged, filter } from 'rxjs/operators';
import { FormArray, FormBuilder, FormControl, FormGroup, FormsModule } from '@angular/forms';
import { FileExportService } from '../../shared/services/file-export.service';
import { ModuleIds } from '../../shared/permission-constants/permission-const';
import { PermissionService } from '../../shared/services/permission.service';

@Component({
  selector: 'app-projects',
  standalone: true,
  imports: [
    CommonModule,
    LayoutComponent,
    MatInputModule,
    AgGridAngular,
    FormsModule,
  ],
  templateUrl: './projects.component.html',
  styleUrl: './projects.component.scss',
})
export class ProjectsListComponent implements OnInit {
  public viewPortSize: boolean = false;
  loading: boolean = false;
  error: any = '';
  rowData = [];
  readonly dialog = inject(MatDialog);
  columnDefs: any[];
  // profileName: any;
  search: any;
  advancedSearch: any;
  private searchSubject = new Subject<string>();
  page = 0;
  pageSize = 10;
  totalPages = 1;
  selectedBranchId: any;
  pageSizeOptions = [5, 10, 20, 50, 100];
  isExportOpen = false;
  exportFormat: string = 'pdf';
  exportLoading: boolean = false;
  exportForm: FormGroup;
  fieldOptions = [
    { key: 'projectName', label: 'Project Name' },
    { key: 'projectCode', label: 'Project Code' },
    { key: 'projectCost', label: 'Project Cost' },
    { key: 'customerData.firstName', label: 'Customer Name' },
    { key: 'orderData.orderCode', label: 'Order Code' },
    { key: 'quoteData.quoteCode', label: 'Quote Code' },
  ];
  canEditProject$ = this.permissionService.hasPermission(
    ModuleIds.Project,
    'Edit'
  );
  canDeleteProject$ = this.permissionService.hasPermission(
    ModuleIds.Project,
    'Delete'
  );
  canDeleteProject:boolean = false;
  canEditProject:boolean = false;

  constructor(
    private router: Router,
    private toastr: ToastrService,
    private projectService: ProjectService,
    private fileExportService: FileExportService,
    private fb: FormBuilder,
    private permissionService: PermissionService
  ) {
    this.exportForm = this.fb.group({
      fields: this.fb.array([]),
    });

  }

  ngOnInit() {
    const selectedBranchId: any = localStorage.getItem('selectedBranchId');
    const branch = JSON.parse(selectedBranchId);
    this.selectedBranchId = branch;
    this.fetchAllProjects();

    this.searchSubject
      .pipe(
        debounceTime(300),
        distinctUntilChanged(),
        filter(
          (searchTerm: any) =>
            searchTerm.trim().length >= 3 || searchTerm.trim().length === 0
        )
      )
      .subscribe((searchTerm) => {
        this.search = searchTerm.trim();
        this.fetchAllProjects();
      });

    this.checkViewportSize();
    this.canDeleteProject$.subscribe(val => {
      this.canDeleteProject = val;
    });
    this.canEditProject$.subscribe(val => {
      this.canEditProject = val;
    });
  }

  defaultMethod: any = {
    FixedCostForProject: 'Fixed Cost For Project',
    HourlyRate: 'Hourly Rate',
  };

  navigateTo(route: string) {
    this.router.navigate([route]);
  }

  isDropdownOpen = false;

  toggleDropdown(event?: MouseEvent) {
    if (event) {
      event.stopPropagation();
    }
    this.isDropdownOpen = !this.isDropdownOpen;
  }

  @HostListener('window:resize', ['$event'])
  onResize(event: any) {
    this.checkViewportSize();
  }

  checkViewportSize() {
    if (window.innerWidth > 1200) {
      this.viewPortSize = true;
    } else {
      this.viewPortSize = false;
    }
    this.setAGColumn();
  }

  setAGColumn() {
    this.columnDefs = [
      {
        headerName: 'Project Name',
        field: 'projectName',
        flex: this.viewPortSize ? 1 : 0,
      },
      {
        headerName: 'Project Code',
        field: 'projectCode',
        flex: this.viewPortSize ? 1 : 0,
      },
      {
        headerName: 'Project Cost',
        field: 'projectCost',
        flex: this.viewPortSize ? 1 : 0,
      },
      {
        headerName: 'Customer Name',
        field: 'customerName',
        flex: this.viewPortSize ? 1 : 0,
      },
      {
        field: 'action',
        headerName: 'Actions',
        flex: this.viewPortSize ? 1 : 0,
        cellRenderer: (params: any) => {
          const container = document.createElement('div');
          container.style.display = 'flex';
          container.style.alignItems = 'center';
          container.style.gap = '20px';

          const editIcon = document.createElement('img');
          editIcon.src = 'assets/images/icons/icon-edit.svg';
          editIcon.style.cursor = 'pointer';
          editIcon.setAttribute('data-action', 'edit');
          if(this.canEditProject){
            container.appendChild(editIcon);
          }

          // this.store.select(selectPermissions.hasPermission('6', Permission.Edit)).subscribe((canEdit) => {
          //   if (canEdit) {
          //     container.appendChild(editIcon);
          //   }
          // });

          const deleteIcon = document.createElement('img');
          deleteIcon.src = 'assets/images/icons/icon-delete.svg';
          deleteIcon.style.cursor = 'pointer';
          deleteIcon.setAttribute('data-action', 'delete');

          if(this.canDeleteProject){
            container.appendChild(deleteIcon);
          }
          // this.store.select(selectPermissions.hasPermission('6', Permission.Delete)).subscribe((canDelete) => {
          //   if (canDelete) {
          //     container.appendChild(deleteIcon);
          //   }
          // });

          return container;
        },
        floatingFilter: false,
        filter: false,
      },
    ];
  }

  public defaultColDef: ColDef = {
    filter: true,
    floatingFilter: true,
    resizable: true,
    sortable: true
  };
  
  public rowSelection: 'single' | 'multiple' = 'multiple';
  public paginationPageSize = 10;
  public paginationPageSizeSelector: number[] | boolean = [10, 25, 50];
  public themeClass: string = 'ag-theme-quartz';
  gridApi: any;

  onGridReady(params: any) {
    this.gridApi = params.api;
  }

  toggleExport(event: MouseEvent) {
    event.stopPropagation();
    this.isExportOpen = !this.isExportOpen;
  }
  
  private fetchAllProjects(pageSize: number = 10) {    
    if (pageSize) {
      this.pageSize = pageSize;
    }
    const skip = this.page * this.pageSize;
    const input = {
      search: this.search,
      limit: this.pageSize,
      advancedSearch: this.advancedSearch,
      skip,
    }
    this.projectService
      .findAllProjectsWithPagination(input)
      .subscribe({
        next: (projects: any) => {
          this.totalPages = Math.ceil(
            (projects?.total || projects?.projects?.length) / this.pageSize
          );

          this.rowData = (projects?.projects || []).map((project: any) => {
            return {
              id: project?.id ?? '--',
              projectName: project?.projectName ?? '--',
              projectCode: project?.projectCode ?? '--',
              projectCost: project?.projectCost
                ? `AED ${project.projectCost.toFixed(2)}`
                : '--',
              customerName: project?.customerData
                ? `${project.customerData.firstName ?? ''} ${project.customerData.lastName ?? ''}`.trim() || '--'
                : '--',
              orderCode: project?.orderData?.orderCode ?? '--',
              quoteCode: project?.quoteData?.quoteCode ?? '--',
            };
          });
        },
        error: (error: any) => {
          this.error = error;
          this.loading = false;
        },
      });
  }

  get exportFieldsArray() {
    return this.exportForm.get('fields') as FormArray;
  }


  onFieldCheckboxChange(event: any, fieldName: string) {
    const fieldsArray: FormArray = this.exportForm.get('fields') as FormArray;

    if (event.target.checked) {
      fieldsArray.push(new FormControl(fieldName));
    } else {
      const index = fieldsArray.controls.findIndex(
        (control) => control.value === fieldName
      );
      if (index >= 0) {
        fieldsArray.removeAt(index);
      }
    }
  }

  
  exportEmployees() {
    const selectedFields = this.exportForm.value.fields;

    if (selectedFields.length === 0) {
      this.toastr.error('Please select at least one field to export.');
      return;
    }

    let exportObservable;
    const input = {
      search: this.search,
    }
    switch (this.exportFormat) {
      case 'pdf':
        exportObservable =
          this.projectService.exportProjectsToPDF(input, selectedFields);
        break;
      case 'csv':
        exportObservable =
          this.projectService.exportProjectsToCSV(input, selectedFields);
        break;
      default:
        this.toastr.error('Invalid export format selected.');
        return;
    }

    this.exportLoading = true;
    // Call the export service method
    exportObservable.subscribe({
      next: (response: Blob) => {
        this.exportLoading = false;
        this.getFile(response, this.exportFormat);
      },
      error: (error) => {
        this.exportLoading = false;
        this.toastr.error(
          'Failed to export employees. Please try again.'
        );
      },
    });
  }

  async getFile(response: any, format: string) {
    try {
      const file = await this.fileExportService.getFileUrlPost(
        response,
        format
      );
    } catch (error) {
      console.error('Error exporting file', error);
    }
  }


  onFilterChanged() {
    const filterModel = this.gridApi.getFilterModel(); // Get filter values from grid
  
    const advancedSearch: any = {
      projectName: filterModel.projectName?.filter || '',
      projectCode: filterModel.projectCode?.filter || '',
      customerName: filterModel.customerName?.filter || '',
      branchName: filterModel.branchName?.filter || '',
      projectCost: filterModel.projectCost?.filter || '',
      orderCode: filterModel.orderCode?.filter || '',
      quoteCode: filterModel.quoteCode?.filter || ''
    };
  
    this.advancedSearch = advancedSearch;
    this.page = 0; // Reset to first page
    this.fetchAllProjects();
  }
  

  nextPage() {
    if (this.page + 1 < this.totalPages) {
      this.page++;
      this.fetchAllProjects(this.pageSize);
    }
  }

  prevPage() {
    if (this.page > 0) {
      this.page--;
      this.fetchAllProjects(this.pageSize);
    }
  }

  onPageSizeChange(event: Event): void {
    const newSize = Number((event.target as HTMLSelectElement).value);
    console.log('New Page Size:', newSize);
    this.pageSize = newSize; // ✅ Correctly update pageSize
    this.page = 0; // ✅ Reset to first page
    this.fetchAllProjects(this.pageSize); // ✅ Use new pageSize
  }

  onCellClicked(params: any) {
    const projectId = params.data?.id;

    if (
      params.column.colId === 'action' &&
      params.event.target.dataset.action
    ) {
      let action = params.event.target.dataset.action;

      if (action === 'edit') {
        this.onEdit(projectId);
      } else if (action === 'delete') {
        this.onConfirmDelete(projectId);
      }
    } else {
      this.router.navigate([`/projects/project-dashboard/${projectId}`]);
    }
  }

  onConfirmDelete(id: number): void {
    console.log(id);

    const dialogRef = this.dialog.open(ConfirmDialog);

    dialogRef.afterClosed().subscribe((result) => {
      if (result === true) {
        this.onDelete(id);
      }
    });
  }
  onDelete(id: any) {
    this.projectService.removeProject(id).subscribe({
      next: (result) => {
        if (result) {
          this.toastr.success('Project deleted successfully', 'Success');
          this.fetchAllProjects();
        }
      },
      error: (error) => {
        this.toastr.error(error, 'Error');
      },
    });
  }
  onEdit(id: any) {
    console.log(' inside the');
    this.router.navigate([`/projects/project-edit/${id}`]);
  }

  onSearch(event: Event) {
    const input = event.target as HTMLInputElement;
    this.pageSize = 10;
    this.page = 0;
    this.searchSubject.next(input.value);
  }
}
